import {
  ChannelType,
  type DirectoryPerson,
  selectDirectory,
} from '@alternativamais/chat-core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { filtrarPorTermo } from '@alternativamais/chat-core';

/** Grupo do Discord: até 10 pessoas, contando quem cria. */
export const MAX_GRUPO = 10;

/**
 * Selecionar pessoas (D3): criar conversa em grupo (do "+" das DMs ou de
 * uma DM, já com a pessoa) ou adicionar ao grupo aberto.
 */
export function NovoGrupo({
  canalId,
  aoFechar,
}: {
  /** DM (vira grupo com a pessoa) ou grupo (adiciona). */
  canalId?: string;
  aoFechar: () => void;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const canal = useEstado((estado) =>
    canalId ? estado.channels[canalId] : undefined,
  );
  const grupo = canal?.type === ChannelType.GROUP_DM;
  const fixos = useMemo(
    () => new Set((canal?.recipients ?? []).map((pessoa) => pessoa.id)),
    [canal],
  );
  const pessoas = useDerivado(selectDirectory, (estado) => [
    estado.members,
    estado.users,
    estado.presences,
  ]);
  const [busca, setBusca] = useState('');
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const vagas = MAX_GRUPO - 1 - fixos.size - escolhidas.length;
  const lista = useMemo(
    () =>
      filtrarPorTermo(
        pessoas.filter((pessoa) => !fixos.has(pessoa.userId)),
        busca,
        (pessoa: DirectoryPerson) => [
          pessoa.name,
          cliente.store.getState().users[pessoa.userId]?.username ?? '',
        ],
        30,
        (pessoa) => (pessoa.status === 'offline' ? 0 : 1),
      ),
    [pessoas, busca, fixos, cliente],
  );

  const alternar = (id: string) =>
    setEscolhidas((atuais) =>
      atuais.includes(id)
        ? atuais.filter((item) => item !== id)
        : vagas > 0
          ? [...atuais, id]
          : atuais,
    );

  const confirmar = async () => {
    if (!escolhidas.length || enviando) return;
    setEnviando(true);
    try {
      if (grupo && canalId) {
        for (const id of escolhidas)
          await cliente.api.addRecipient(canalId, id);
        aoFechar();
        return;
      }
      const todos = [...fixos, ...escolhidas];
      const criado =
        todos.length === 1
          ? await cliente.openDm(todos[0])
          : await cliente.api.openPrivateChannel({ recipients: todos });
      cliente.store.getState().upsertChannel(criado);
      aoFechar();
      navegar(`/canais/@me/${criado.id}`);
    } catch (erro) {
      avisarErro(erro);
      setEnviando(false);
    }
  };

  const nomeDe = (id: string) =>
    pessoas.find((pessoa) => pessoa.userId === id)?.name ??
    cliente.store.getState().users[id]?.username ??
    '';

  return (
    <div className="flex w-[440px] max-w-[calc(100vw-16px)] flex-col rounded-xl border border-borda bg-elevado shadow-[0_12px_32px_rgba(0,0,0,.45)]">
      <div className="flex flex-col gap-1 px-4 pt-4">
        <h2 className="m-0 text-lg font-semibold">
          {grupo ? 'Adicionar ao grupo' : 'Selecionar pessoas'}
        </h2>
        <p className="m-0 text-sm text-texto-3">
          {vagas > 0
            ? `Você pode adicionar mais ${vagas} ${vagas === 1 ? 'pessoa' : 'pessoas'}.`
            : 'O grupo chegou ao limite de 10 pessoas.'}
        </p>
      </div>
      <div className="m-4 mb-2 flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg bg-rail px-2 py-1.5">
        {escolhidas.map((id) => (
          <span
            key={id}
            className="flex h-7 items-center gap-1 rounded-md bg-ativo pr-1 pl-2 text-sm"
          >
            {nomeDe(id)}
            <button
              type="button"
              aria-label={`Remover ${nomeDe(id)}`}
              onClick={() => alternar(id)}
              className="flex size-5 items-center justify-center rounded text-texto-3 hover:text-texto"
            >
              <Icone nome="x" tamanho={12} />
            </button>
          </span>
        ))}
        <label className="flex min-w-32 grow">
          <span className="sr-only">Buscar pessoas pelo nome ou usuário</span>
          <input
            autoFocus
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Backspace' && !busca && escolhidas.length) {
                setEscolhidas((atuais) => atuais.slice(0, -1));
              }
            }}
            placeholder={escolhidas.length ? '' : 'Digite o nome de alguém'}
            className="h-7 w-full border-0 bg-transparent text-sm text-texto outline-none placeholder:text-texto-3"
          />
        </label>
      </div>
      <ul
        aria-label="Pessoas"
        className="rolagem m-0 flex max-h-72 list-none flex-col overflow-y-auto px-2 pb-2"
      >
        {lista.map((pessoa) => (
          <Pessoa
            key={pessoa.userId}
            pessoa={pessoa}
            marcada={escolhidas.includes(pessoa.userId)}
            aoAlternar={() => alternar(pessoa.userId)}
          />
        ))}
        {!lista.length && (
          <li className="px-2 py-3 text-sm text-texto-3">
            Ninguém com esse nome.
          </li>
        )}
      </ul>
      <div className="border-t border-borda p-3">
        <button
          type="button"
          disabled={!escolhidas.length || enviando}
          onClick={() => void confirmar()}
          className="h-10 w-full rounded-lg bg-destaque text-sm font-semibold text-destaque-texto disabled:opacity-50"
        >
          {grupo
            ? 'Adicionar ao grupo'
            : escolhidas.length + fixos.size > 1
              ? 'Criar conversa em grupo'
              : 'Abrir conversa'}
        </button>
      </div>
    </div>
  );
}

function Pessoa({
  pessoa,
  marcada,
  aoAlternar,
}: {
  pessoa: DirectoryPerson;
  marcada: boolean;
  aoAlternar: () => void;
}) {
  const usuario = useEstado((estado) => estado.users[pessoa.userId]);
  if (!usuario) return null;
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={marcada}
        onClick={aoAlternar}
        className="flex h-11 w-full items-center gap-3 rounded-md px-2 text-left hover:bg-ativo"
      >
        <Avatar
          pessoa={usuario}
          nome={pessoa.name}
          tamanho={32}
          status={pessoa.status}
          fundo="var(--cor-elevado)"
        />
        <span className="flex min-w-0 grow flex-col">
          <span className="truncate text-[15px] font-semibold">
            {pessoa.name}
          </span>
          <span className="truncate text-xs text-texto-3">
            {usuario.username}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={`flex size-5 shrink-0 items-center justify-center rounded border ${
            marcada
              ? 'border-destaque bg-destaque text-destaque-texto'
              : 'border-texto-3'
          }`}
        >
          {marcada && <Icone nome="check" tamanho={14} />}
        </span>
      </button>
    </li>
  );
}
